'use client'

import { useId } from 'react'
import { Check } from 'lucide-react'
import { PaymentMethodIcon, type PaymentMethodKind } from '@sifly/shared-ui/quote'
import {
  BUILT_IN_MANUAL_METHODS,
  manualMethodLabel,
  normalizePaymentSnapshot,
  type ManualPaymentMethod,
  type PaymentMethodsSnapshot,
} from '@sifly/shared-types'

/** What the customer picked. `otherId` only carries meaning when
 *  `method === 'other'` — it names which of the dealer's custom methods. */
export type ManualMethodSelection = { method: ManualPaymentMethod; otherId?: string }

type ManualMethodItem = { method: ManualPaymentMethod; otherId?: string; label: string }

/** Labels come from the one registry (@sifly/shared-types paymentMethods.ts),
 *  the same source the pay page's panel reads, so a method is never named two
 *  ways across the cart and the pay page. */
export function manualMethodItems(stored: PaymentMethodsSnapshot): ManualMethodItem[] {
  // An order snapshot saved before a method existed lacks its key; read it as off.
  const snapshot = normalizePaymentSnapshot(stored)
  const items: ManualMethodItem[] = []
  for (const method of BUILT_IN_MANUAL_METHODS) {
    if (snapshot[method].enabled) items.push({ method, label: manualMethodLabel(method) })
  }
  for (const o of snapshot.other) {
    // Redundant against the cart's own snapshot as of the redaction fix —
    // `cartVisibleMethods` now FILTERS unpublished/unusable custom methods out
    // rather than shipping them with `enabled: false`, so nothing false can
    // reach here from the layout. Kept as defence in depth: this component
    // takes a `PaymentMethodsSnapshot`, and an unfiltered one (an order's own
    // `payment_options.methods`, say) is a perfectly valid value for that type.
    if (o.enabled && o.label.trim()) {
      items.push({ method: 'other', otherId: o.id, label: manualMethodLabel('other', o.label) })
    }
  }
  return items
}

function isSelected(selected: ManualMethodSelection | null, item: ManualMethodItem): boolean {
  if (!selected || selected.method !== item.method) return false
  return item.method === 'other' ? selected.otherId === item.otherId : true
}

/**
 * The dealer's non-Stripe payment methods, offered as an extra set of
 * toggle rows below the card/ACH choice in `CartDrawer`. Selected-state
 * treatment is copied verbatim from the existing pay-method buttons
 * (`CartDrawer.tsx` ~L556-586) so the two lists read as one control.
 *
 * Renders nothing when the dealer has enabled none of these — the common
 * case today, and the regression that matters most: an untouched cart for
 * every dealer who hasn't turned this on.
 */
export default function ManualMethodList({
  snapshot,
  selected,
  onSelect,
  caption = 'Other ways to pay',
}: {
  snapshot: PaymentMethodsSnapshot
  selected: ManualMethodSelection | null
  onSelect: (selection: ManualMethodSelection) => void
  /** "Other ways to pay" beside card/bank; "Ways to pay" when these are all there is. */
  caption?: string
}) {
  const items = manualMethodItems(snapshot)
  const captionId = useId()

  if (items.length === 0) return null

  return (
    <div className="space-y-2" role="group" aria-labelledby={captionId}>
      <p id={captionId} className="text-xs font-medium text-[var(--color-text-secondary)]">
        {caption}
      </p>
      {items.map((item) => {
        const key = item.method === 'other' ? `other:${item.otherId}` : item.method
        const active = isSelected(selected, item)
        const kind: PaymentMethodKind = item.method
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect({ method: item.method, otherId: item.otherId })}
            aria-pressed={active}
            className={`w-full flex items-start justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${
              active
                ? 'border-[var(--color-selected)] bg-[var(--color-selected-soft)]'
                : 'border-[var(--color-border)] hover:bg-[var(--color-surface-muted)]'
            }`}
          >
            <span className="flex items-start gap-2.5 min-w-0">
              <PaymentMethodIcon
                kind={kind}
                size={16}
                className="text-[var(--color-text-muted)] mt-0.5 flex-shrink-0"
                aria-hidden
              />
              <span className="block text-sm font-medium text-[var(--color-text)] truncate">{item.label}</span>
            </span>
            {active && <Check size={16} className="text-[var(--color-selected-readable)] mt-0.5 flex-shrink-0" />}
          </button>
        )
      })}
    </div>
  )
}
